import { AppError } from "./errors";

export const CURRENT_TERMS_VERSION = "2.2";
export const CURRENT_PRIVACY_POLICY_VERSION = "2026-08-05";

export const LEGAL_CONSENT_REQUIRED_MESSAGE =
  "利用規約とプライバシーポリシーへの同意を確認してください。";
export const LEGAL_CONSENT_VERSION_MISMATCH_MESSAGE =
  "利用規約等が更新されました。画面を再読み込みして内容をご確認ください。";

const PENDING_LEGAL_CONSENT_KEY = "wizmate.registration.legal-consent.v1";
const DEFAULT_LEGAL_RETURN_TO = "/home";

export type RegistrationLegalReturnTo =
  | "/register"
  | "/line"
  | "/line-register"
  | "/email-otp/email?mode=register"
  | "/email-otp/verify";

const ALLOWED_LEGAL_RETURN_TO = new Set<RegistrationLegalReturnTo>([
  "/register",
  "/line",
  "/line-register",
  "/email-otp/email?mode=register",
  "/email-otp/verify",
]);

export type RegistrationLegalConsent = {
  legal_consent_accepted: true;
  age_requirement_confirmed: true;
  terms_version: typeof CURRENT_TERMS_VERSION;
  privacy_policy_version: typeof CURRENT_PRIVACY_POLICY_VERSION;
};

export function createRegistrationLegalConsent(): RegistrationLegalConsent {
  return {
    legal_consent_accepted: true,
    age_requirement_confirmed: true,
    terms_version: CURRENT_TERMS_VERSION,
    privacy_policy_version: CURRENT_PRIVACY_POLICY_VERSION,
  };
}

export function buildRegistrationLegalDocumentHref(
  documentPath: "/terms" | "/privacy",
  returnTo: RegistrationLegalReturnTo
): string {
  return `${documentPath}?returnTo=${encodeURIComponent(returnTo)}`;
}

export function resolveRegistrationLegalReturnTo(value: string | null | undefined): string {
  return value && ALLOWED_LEGAL_RETURN_TO.has(value as RegistrationLegalReturnTo)
    ? value
    : DEFAULT_LEGAL_RETURN_TO;
}

export function formatJapaneseLegalDate(version: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(version);
  if (!match) return version;
  return `${Number(match[1])}年${Number(match[2])}月${Number(match[3])}日`;
}

export function savePendingLegalConsent(): void {
  sessionStorage.setItem(PENDING_LEGAL_CONSENT_KEY, JSON.stringify(createRegistrationLegalConsent()));
}

export function readPendingLegalConsent(): RegistrationLegalConsent | null {
  const rawValue = sessionStorage.getItem(PENDING_LEGAL_CONSENT_KEY);
  if (!rawValue) return null;

  try {
    const value = JSON.parse(rawValue) as Partial<RegistrationLegalConsent>;
    if (
      value.legal_consent_accepted !== true ||
      value.age_requirement_confirmed !== true ||
      value.terms_version !== CURRENT_TERMS_VERSION ||
      value.privacy_policy_version !== CURRENT_PRIVACY_POLICY_VERSION
    ) {
      return null;
    }
    return createRegistrationLegalConsent();
  } catch {
    return null;
  }
}

export function clearPendingLegalConsent(): void {
  sessionStorage.removeItem(PENDING_LEGAL_CONSENT_KEY);
}

export function registrationLegalErrorMessage(error: unknown): string | null {
  if (!(error instanceof AppError)) return null;

  const diagnostic = `${error.message} ${error.meta.responseText ?? ""}`.toLowerCase();
  if (
    diagnostic.includes("terms_version") ||
    diagnostic.includes("privacy_policy_version") ||
    diagnostic.includes("version_mismatch") ||
    diagnostic.includes("version mismatch")
  ) {
    return LEGAL_CONSENT_VERSION_MISMATCH_MESSAGE;
  }
  if (
    diagnostic.includes("legal_consent") ||
    diagnostic.includes("age_requirement") ||
    diagnostic.includes("consent_required") ||
    diagnostic.includes("consent required")
  ) {
    return LEGAL_CONSENT_REQUIRED_MESSAGE;
  }
  return null;
}
